import { STORAGE_KEYS } from '@/config/storageKeys'
import { useCallback, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

interface UseThemeReturn {
  theme: Theme
  isDark: boolean
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
}

function getSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null
  const stored = localStorage.getItem(STORAGE_KEYS.THEME_PREFERENCE)
  return stored === 'dark' || stored === 'light' ? stored : null
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return

  if (theme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

export function useTheme(): UseThemeReturn {
  const [theme, setThemeState] = useState<Theme>(() => {
    return getStoredTheme() ?? getSystemTheme()
  })

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const setTheme = useCallback(
    (newTheme: Theme) => {
      if (newTheme === theme) return
      setThemeState(newTheme)
      localStorage.setItem(STORAGE_KEYS.THEME_PREFERENCE, newTheme)
    },
    [theme]
  )

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }, [theme, setTheme])

  return {
    theme,
    isDark: theme === 'dark',
    toggleTheme,
    setTheme,
  }
}
